import React, { lazy, useMemo } from "react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import "./App.css";
import { AdminAccessRequired } from "./components/auth/AdminAccessRequired";
import { AuthenticationRequired } from "./components/auth/AuthenticationRequired";
import PWSForUsers from "./components/auth/PWSForUsers";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { RegisteredUser } from "./components/auth/RegisteredUser";
import { SuspenseContainer } from "./components/container/SuspenseContainer";
import { DummyLayout as PWSForAdminTools } from "./components/layout/DummyLayout";
import PWSChildRootLayoutPage from "./components/layout/PWSChildRootLayout";
import RootLayout from "./components/layout/RootLayout";
import {
  IMPORT_KITS,
  PWS_BY_DISTRICT_OFFICES,
  PWS_CONTACTS_DETAILS,
  PWS_LOOKUP,
  REPORTS,
  SCHEDULE_GOALS,
  SCHEDULE_SAMPLERS,
  VIDEOS,
  WEEKLY_SAMPLE_KIT,
  WELLS_CONTACTS,
  IMPORT_RESULTS,
  IMPORT_FIELD_DATA,
  WELL_LIST,
  FIELD_DATA_EDD,
} from "./constants/constant";
import { useUser } from "./context/UserContext";
import About from "./pages/About";
import Maintenance from "./pages/Maintenance";
import ContactUs from "./pages/ContactUs";
import ErrorPage from "./pages/ErrorPage";
import Home from "./pages/Home";
import Resources from "./pages/Resources";
import SignIn from "./pages/SignIn";
import CustodyChain, {
  loader as getPWSNameByPWSID,
} from "./pages/coc/CustodyChain";
import TrainingVideos from "./pages/tools/TrainingVideos";
import WaterSystemRootLayoutPage from "./pages/waterSystem/WaterSystemRootLayout";
import { TOOLS_TO_VENDOR_MAPPING } from "./utility/routingInformation";
import ImportKits from "./pages/tools/ImportKits";
import { ImportResults } from "./pages/tools/ImportResults";
import ImportFieldData from "./pages/tools/ImportFieldData";

const LazyDashboard = lazy(() => import("./pages/Dashboard"));
const LazyAdminTools = lazy(() => import("./pages/AdminTools"));
const LazyReportAdminTool = lazy(() => import("./pages/tools/Reports"));
const LazyFieldDataEDDReport = lazy(() =>
  import("./pages/tools/FieldDataEDDReport")
);

const LazyAllPWSAdminTool = lazy(() => import("./pages/tools/PWSLookup"));
const LazyWellList = lazy(() => import("./pages/tools/WellList"));
const LazySWBDataListsAdminToolPage = lazy(() =>
  import("./pages/tools/WellsContactsSWB")
);
const LazyPWSContactsPage = lazy(() => import("./pages/tools/PWSContacts"));
const LazySamplersSchedule = lazy(() =>
  import("./pages/tools/SamplersSchedule")
);
const LazyScheduleGoalsPage = lazy(() => import("./pages/tools/ScheduleGoals"));
const LazyFieldDataEditPage = lazy(() =>
  import("./pages/waterSystem/well/visit/FieldDataEdit").then((module) => ({
    default: module.FieldDataEditPage,
  }))
);
const LazyLabShippment = lazy(() => import("./pages/tools/WeeklySampleKit"));
const LazyDistrictOfficesPWSTool = lazy(() =>
  import("./pages/tools/PWSByDistrictOffice")
);
const LazyNotesPage = lazy(() => import("./pages/waterSystem/Notes"));
const LazySamplesEditPage = lazy(() =>
  import("./pages/waterSystem/well/visit/SamplesEdit")
);
const LazySamplesViewPage = lazy(() =>
  import("./pages/waterSystem/well/visit/SamplesView")
);
const LazyFieldDataViewPage = lazy(() =>
  import("./pages/waterSystem/well/visit/FieldDataView")
);
const LazyVisitsDescription = lazy(() => import("./pages/VisitsDescription"));
const LazyCreateAccount = lazy(() => import("./pages/CreateAccount"));
const LazyAcknowledgement = lazy(() =>
  import("./pages/waterSystem/Acknowledgement")
);
const LazyProfile = lazy(() => import("./pages/Profile"));
const LazyFilesPage = lazy(() =>
  import("./pages/waterSystem/well/visit/Files")
);

const LazyKeyPersonnelPage = lazy(() =>
  import("./pages/waterSystem/KeyPersonnel")
);
const LazyWaterSystemPage = lazy(() =>
  import("./pages/waterSystem/WaterSystem")
);
const LazyWellPage = lazy(() => import("./pages/waterSystem/well/Well"));
const LazyVisitPage = lazy(() =>
  import("./pages/waterSystem/well/visit/Visit")
);

/** VIKAS--
 * Asynchronously imports and executes the getWaterSystemCount loader function.
 * @param {string} user_email - The email of the user to get the water system count for.
 * @returns {Promise<number>} - The water system count associated with the user.
 */
const getWaterSystemCount = async (user_email) => {
  const { loader: getWaterSystemCount } = await import("./pages/Dashboard");
  return getWaterSystemCount(user_email);
};

/** VIKAS--
 * PWS routes
 * This is the top level route for the Water System Layout
 * It has a dynamic parameter for the pwsId and pwsKey
 * The pwsId and pwsKey are used to fetch the water system's data
 * The children routes are
 *  - notes: Shows the notes page for the water system
 *  - keyPersonnel: Shows the key personnel page for the water system
 *  - well: Shows the well page for the water system
 *    - notes: Shows the notes page for the well
 *    - visit: Shows the visit page for the well
 *      - notes: Shows the notes page for the visit
 *      - samplesEdit: Shows the samples page for the visit
 *        - notes: Shows the notes page for the visit
 *      - fieldDataFormEdit: Shows the field data form page for the visit
 *        - notes: Shows the notes page for the visit
 *      - filesEdit/View: Shows the files page for the visit
 *        - notes: Shows the notes page for the visit
 *      - fieldDataView: Shows the field data page for the visit
 *      - samplesCollectedView: Shows the samples collected page for the visit
 */
const PWS_ROUTES = {
  path: ":pwsId/:pwsKey",
  element: <WaterSystemRootLayoutPage />,
  children: [
    {
      index: true,
      element: (
        <SuspenseContainer>
          <LazyWaterSystemPage />
        </SuspenseContainer>
      ),
    },
    {
      path: "notes",
      element: (
        <SuspenseContainer>
          <LazyNotesPage type="pws" />,
        </SuspenseContainer>
      ),
    },
    {
      path: "keyPersonnel",
      element: (
        <SuspenseContainer>
          <LazyKeyPersonnelPage />
        </SuspenseContainer>
      ),
    },
    {
      path: "well/:wellId/:wellKey",
      element: <PWSChildRootLayoutPage />,
      children: [
        {
          index: true,
          element: (
            <SuspenseContainer>
              <LazyWellPage />
            </SuspenseContainer>
          ),
        },
        {
          path: "notes",
          element: (
            <SuspenseContainer>
              <LazyNotesPage type="well" />,
            </SuspenseContainer>
          ),
        },
        {
          path: "visit/:visitId",
          element: <PWSChildRootLayoutPage />,
          children: [
            {
              index: true,
              element: (
                <SuspenseContainer>
                  <LazyVisitPage />
                </SuspenseContainer>
              ),
            },
            {
              path: "notes",
              element: (
                <SuspenseContainer>
                  <LazyNotesPage type="visit" />,
                </SuspenseContainer>
              ),
            },
            {
              path: "samples",
              element: <PWSChildRootLayoutPage />,
              children: [
                {
                  index: true,
                  element: (
                    <SuspenseContainer>
                      <LazySamplesEditPage />,
                    </SuspenseContainer>
                  ),
                },
                {
                  path: "notes",
                  element: (
                    <SuspenseContainer>
                      <LazyNotesPage type="visit" />,
                    </SuspenseContainer>
                  ),
                },
              ],
            },
            {
              path: "fieldDataForm",
              element: <PWSChildRootLayoutPage />,
              children: [
                {
                  index: true,
                  element: (
                    <SuspenseContainer>
                      <LazyFieldDataEditPage />
                    </SuspenseContainer>
                  ),
                },
                {
                  path: "notes",
                  element: (
                    <SuspenseContainer>
                      <LazyNotesPage type="visit" />,
                    </SuspenseContainer>
                  ),
                },
              ],
            },
            {
              path: "files",
              element: <PWSChildRootLayoutPage />,
              children: [
                {
                  index: true,
                  element: (
                    <SuspenseContainer>
                      <LazyFilesPage />
                    </SuspenseContainer>
                  ),
                },
                {
                  path: "notes",
                  element: (
                    <SuspenseContainer>
                      <LazyNotesPage type="visit" />,
                    </SuspenseContainer>
                  ),
                },
              ],
            },
            {
              path: "fieldData",
              element: (
                <SuspenseContainer>
                  <LazyFieldDataViewPage />
                </SuspenseContainer>
              ),
            },
            {
              path: "samplesCollected",
              element: (
                <SuspenseContainer>
                  <LazySamplesViewPage />
                </SuspenseContainer>
              ),
            },
          ],
        },
      ],
    },
  ],
};

/** VIKAS--
 * PWS_ROUTES is an object that contains the routes for the Protected Water Source
 * pages. This object is frozen to prevent any accidental changes to the routes.
 */
Object.freeze(PWS_ROUTES);

const App = () => {
  const { userInfo } = useUser();
  const { user_email } = userInfo;

  const maintenance = false;
  // const maintenance = true;

  const router = useMemo(() => createBrowserRouter([
    {
      path: "/",
      element: maintenance ? <Maintenance /> : <RootLayout />, // Check if maintenance is true
      children: maintenance
        ? [] // If maintenance is true, no child routes will be defined, it just goes to the Maintenance page
        : [
          {
            index: true,
            element: <Home />,
          },
          // { path: "maintenance", element: <Maintenance /> },
          { path: "resources", element: <Resources /> },
          { path: "about", element: <About /> },
          { path: "contact", element: <ContactUs /> },
          { path: "signin", element: <SignIn /> },
          {
            path: "schedule-confirmation/:requestId",
            element: (
              <SuspenseContainer>
                <LazyVisitsDescription />
              </SuspenseContainer>
            ),
          },
          {
            element: <AuthenticationRequired />,
            // VIKAS-- This route is protected and requires the user to be authenticated
            children: [
              {
                path: "create-account",
                element: (
                  <SuspenseContainer>
                    <LazyCreateAccount />
                  </SuspenseContainer>
                ),
                // VIKAS-- This route is protected and only accessible to unregistered users
              },
              {
                element: <RegisteredUser />,
                // VIKAS-- This route is protected and requires the user to be registered
                children: [
                  {
                    path: "profile",
                    element: (
                      <SuspenseContainer>
                        <LazyProfile />
                      </SuspenseContainer>
                    ),
                  },
                  {
                    path: "dashboard",
                    element: (
                      <SuspenseContainer>
                        <LazyDashboard />
                      </SuspenseContainer>
                    ),
                    loader: () => getWaterSystemCount(user_email),
                  },
                  {
                    path: "acknowledgement/:pwsId/:pwsKey",
                    element: (
                      <SuspenseContainer>
                        <LazyAcknowledgement />
                      </SuspenseContainer>
                    ),
                    /** VIKAS--
                     * This route is to acknowledge the water system that you have, and PWSForUsers,
                     * below route is responsible to navigate a user to this route
                     */
                  },
                  {
                    path: "pws",
                    element: <PWSForUsers />,
                    // VIKAS-- This route is protected and only accessible to user who has a water system and has access to it in the database
                    children: [PWS_ROUTES],
                  },
                  {
                    path: ":vendor",
                    element: <AdminAccessRequired />,
                    // VIKAS-- This route is for the users who has admin access/or belong to any vendor group
                    children: [
                      {
                        index: true,
                        element: (
                          <SuspenseContainer>
                            <LazyAdminTools />
                          </SuspenseContainer>
                        ),
                        // VIKAS-- This route is for the tools page (and the tools are shown based on the vendor tile you are clicking on the dashboard)
                        /** VIKAS--
                         * NOTE: All the siblings mentioned below has a ProtectedRoute component
                         * which is responsible for checking using TOOLS_TO_VENDOR_MAPPING for that tool
                         * if the user group has access to the tool, this is required since user can manually
                         * type in the url to access the tool
                         */
                      },
                      {
                        path: "admin-all-pws-info",
                        element: (
                          <ProtectedRoute
                            roles={TOOLS_TO_VENDOR_MAPPING[PWS_LOOKUP]}
                          />
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <SuspenseContainer>
                                <LazyAllPWSAdminTool />,
                              </SuspenseContainer>
                            ),
                          },
                          {
                            path: "pws",
                            element: <PWSForAdminTools />,
                            // VIKAS-- This route is to access the PWS page for admin/vendors and they don't have to acknowledge it
                            children: [PWS_ROUTES],
                          },
                        ],
                      },
                      {
                        path: "admin-report",
                        element: (
                          <ProtectedRoute
                            roles={TOOLS_TO_VENDOR_MAPPING[REPORTS]}
                          />
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <SuspenseContainer>
                                <LazyReportAdminTool />,
                              </SuspenseContainer>
                            ),
                          },
                        ],
                      },
                      {
                        path: "admin-swb-data-list",
                        element: (
                          <ProtectedRoute
                            roles={TOOLS_TO_VENDOR_MAPPING[WELLS_CONTACTS]}
                          />
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <SuspenseContainer>
                                <LazySWBDataListsAdminToolPage />,
                              </SuspenseContainer>
                            ),
                          },
                        ],
                      },
                      {
                        path: "admin-pws-contacts-details",
                        element: (
                          <ProtectedRoute
                            roles={
                              TOOLS_TO_VENDOR_MAPPING[PWS_CONTACTS_DETAILS]
                            }
                          />
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <SuspenseContainer>
                                <LazyPWSContactsPage />,
                              </SuspenseContainer>
                            ),
                          },
                        ],
                      },
                      {
                        path: "schedule",
                        element: (
                          <ProtectedRoute
                            roles={TOOLS_TO_VENDOR_MAPPING[SCHEDULE_GOALS]}
                          />
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <SuspenseContainer>
                                <LazyScheduleGoalsPage />,
                              </SuspenseContainer>
                            ),
                          },
                        ],
                      },
                      {
                        path: "samplers-scheduled",
                        element: (
                          <ProtectedRoute
                            roles={TOOLS_TO_VENDOR_MAPPING[SCHEDULE_SAMPLERS]}
                          />
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <SuspenseContainer>
                                <LazySamplersSchedule />,
                              </SuspenseContainer>
                            ),
                          },
                          {
                            path: ":visitId/fieldDataForm",
                            element: <WaterSystemRootLayoutPage />,
                            children: [
                              {
                                index: true,
                                element: (
                                  <SuspenseContainer>
                                    <LazyFieldDataEditPage />,
                                  </SuspenseContainer>
                                ),
                                // VIKAS-- This route is to edit the field form data
                              },
                              {
                                path: "notes",
                                element: <LazyNotesPage type="visit" />,
                              },
                            ],
                          },
                        ],
                      },
                      {
                        path: "lab-shippment",
                        element: (
                          <ProtectedRoute
                            roles={TOOLS_TO_VENDOR_MAPPING[WEEKLY_SAMPLE_KIT]}
                          />
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <SuspenseContainer>
                                <LazyLabShippment />,
                              </SuspenseContainer>
                            ),
                          },
                        ],
                      },
                      {
                        path: "district-office-pws-info",
                        element: (
                          <ProtectedRoute
                            roles={
                              TOOLS_TO_VENDOR_MAPPING[PWS_BY_DISTRICT_OFFICES]
                            }
                          />
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <SuspenseContainer>
                                <LazyDistrictOfficesPWSTool />
                              </SuspenseContainer>
                            ),
                          },
                          {
                            path: "pws",
                            element: <PWSForAdminTools />,
                            // VIKAS-- This route is to access the PWS page for admin/vendors and they don't have to acknowledge it
                            children: [PWS_ROUTES],
                          },
                        ],
                      },
                      {
                        path: "videos",
                        element: (
                          <ProtectedRoute
                            roles={TOOLS_TO_VENDOR_MAPPING[VIDEOS]}
                          />
                        ),
                        children: [
                          {
                            index: true,
                            element: <TrainingVideos />,
                          },
                        ],
                      },
                      {
                        path: "import-kits",
                        element: (
                          <ProtectedRoute
                            roles={TOOLS_TO_VENDOR_MAPPING[IMPORT_KITS]}
                          />
                        ),
                        children: [
                          {
                            index: true,
                            element: <ImportKits />,
                          },
                        ],
                      },
                      {
                        path: "import-results",
                        element: (
                          <ProtectedRoute
                            roles={TOOLS_TO_VENDOR_MAPPING[IMPORT_RESULTS]}
                          />
                        ),
                        children: [
                          {
                            index: true,
                            element: <ImportResults />,
                          },
                        ],
                      },
                      {
                        path: "import-field-data",
                        element: (
                          <ProtectedRoute
                            roles={TOOLS_TO_VENDOR_MAPPING[IMPORT_FIELD_DATA]}
                          />
                        ),
                        children: [
                          {
                            index: true,
                            // element: <div>Under development</div>,
                            element: <ImportFieldData />,
                          },
                        ],
                      },
                      {
                        path: "field-data-edd-report",
                        element: (
                          <ProtectedRoute
                            roles={TOOLS_TO_VENDOR_MAPPING[FIELD_DATA_EDD]}
                          />
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <SuspenseContainer>
                                <LazyFieldDataEDDReport />,
                              </SuspenseContainer>
                            ),
                          },
                        ],
                      },
                      {
                        path: "well-list-update",
                        element: (
                          <ProtectedRoute
                            roles={TOOLS_TO_VENDOR_MAPPING[WELL_LIST]}
                          />
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <SuspenseContainer>
                                <LazyWellList />,
                              </SuspenseContainer>
                            ),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      errorElement: <ErrorPage />,
    },
    {
      path: "/coc",
      children: [
        {
          path: "visit/:visitId/:pwsname/:kit",
          element: <CustodyChain />,
        },
        {
          path: "blank/:kit",
          element: <CustodyChain />,
        },
      ],
    },
    // { path: "*", element: <Maintenance /> },
  ]), [user_email]);
  return <RouterProvider router={router} />;
};

export default App;
